import {
  AddCircle,
  Cancel,
  CheckCircleOutlined,
  Flaky,
} from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  ProcessAgent,
  ProcessApprovalStep,
  ProcessItem,
  ProcessItemBadge,
} from '~/components/WorkflowProposalProcess/components';
import {
  ApprovalStepState,
  ApprovalStepStatus,
  ProposalProps,
  ProposalStatus,
} from '~/data/proposals';

import { useSxStyles } from './useStyles';
import { useWorkflowProposalProcess } from './useWorkflowProposalProcess';

type PropsType = {
  proposal: ProposalProps;
  onDecision: (decision: ApprovalStepState) => void;
  onUltimateDecision?: (decision: ApprovalStepState[]) => void;
  isPreview?: boolean;
  autoCompleteOnResponsibleHR?: boolean;
};

export const WorkflowProposalProcess = (props: PropsType) => {
  const {
    proposal: rawProposal,
    onDecision,
    onUltimateDecision,
    isPreview,
    autoCompleteOnResponsibleHR,
  } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const {
    proposal,
    getApprovalAgencyOnStep,
    getStepDecision,
    approvalAgencyAsUltimate,
    getUltimateDecision,
    hideUltimateAction,
  } = useWorkflowProposalProcess({
    proposal: rawProposal,
    substituteAsUltimateApprover: !!onUltimateDecision,
    autoCompleteOnResponsibleHR,
  });

  const isInProgress = proposal.proposalStatus === ProposalStatus.IN_PROGRESS;
  const isConcluded = [
    ProposalStatus.COMPLETED,
    ProposalStatus.FAILED,
  ].includes(proposal.proposalStatus);

  const handleDecision = (stepSeq: number, newStatus: ApprovalStepStatus) => {
    const stepDecision = getStepDecision(stepSeq, newStatus);

    if (!!stepDecision) {
      onDecision(stepDecision);
    }
  };

  const handleUltimateDecision = (newStatus: ApprovalStepStatus) => {
    if (!!onUltimateDecision) {
      const stepDecisions = getUltimateDecision(newStatus);

      if (stepDecisions.length > 0) {
        onUltimateDecision(stepDecisions);
      }
    }
  };

  const showUltimateAction =
    !!approvalAgencyAsUltimate &&
    !hideUltimateAction &&
    isInProgress &&
    !isPreview;

  return (
    <Box sx={sxStyles.root}>
      <ProcessItem
        itemBadge={
          <ProcessItemBadge color={'info'}>
            <AddCircle color={'info'} />
          </ProcessItemBadge>
        }
        isFirstItem={true}
        isLastItem={proposal.approvalSteps?.length < 1}
        status={t('workflowInitiator')}
        statusColor={'info'}
        timestamp={proposal.createdTime}
      >
        <ProcessAgent agent={proposal.initiator} />
      </ProcessItem>
      {proposal.approvalSteps?.map((step, index, steps) => (
        <ProcessApprovalStep
          loggedUserAgency={getApprovalAgencyOnStep(step.seq)}
          onDecision={handleDecision}
          step={step}
          key={`proposalStep-${index}`}
          // isFirstStep={index === 0}
          isLastStep={steps.length === index + 1}
          substituteAsUltimateApprover={!!approvalAgencyAsUltimate}
          isPreview={isPreview}
          isConcluded={isConcluded}
        />
      ))}
      {showUltimateAction && (
        <Box sx={sxStyles.ultimateApproval}>
          <Box sx={sxStyles.ultimateApprovalInfo}>
            <Typography>Perform ultimate decision</Typography>
            <Typography variant={'caption'}>
              Complete the workflow process, without waiting other's approval.
            </Typography>
          </Box>

          <ProcessItem
            itemBadge={
              <ProcessItemBadge>
                <Flaky color={'info'} />
              </ProcessItemBadge>
            }
            status={t('complete')}
            statusColor={'info'}
            isFirstItem={true}
            isLastItem={true}
          >
            <ProcessAgent agent={approvalAgencyAsUltimate} />

            <Box sx={sxStyles.approveStep}>
              <Button
                color={'success'}
                startIcon={<CheckCircleOutlined />}
                onClick={() =>
                  handleUltimateDecision(ApprovalStepStatus.APPROVED)
                }
                data-testid="ultimate-approve-button"
              >
                {t('approve')}
              </Button>
              <Button
                color={'error'}
                startIcon={<Cancel />}
                onClick={() =>
                  handleUltimateDecision(ApprovalStepStatus.REJECTED)
                }
                data-testid="ultimate-reject-button"
              >
                {t('reject')}
              </Button>
            </Box>
          </ProcessItem>
        </Box>
      )}
    </Box>
  );
};
